<template>
  <div class="cheatsheet" style="max-width: 800px; margin: auto;">
    <h1>Tenses Comparison: English vs Spanish</h1>
    <div class="tense-table mb-5">
      <div class="tense-row" v-for="(tense, index) in tenses" :key="index">
        <div class="tense-column">
          <h3>{{ tense.name }}</h3>
          <p>🇬🇧 {{ tense.english }}</p>
          <p>🇪🇸 {{ tense.spanish }}</p>
          <p>
            <em>{{ tense.usage }}</em>
          </p>
        </div>
      </div>
    </div>
    <div class="key-notes">
      <h2>Key Notes for Spanish Learners:</h2>
      <ul class="list-group">
        <li class="list-group-item list-group-item-action">
          <h4 class="d-block w-100"
            >Use of <em>haber</em>:</h4
          >
          <div class="">
            Spanish tenses like the Present Perfect, Past Perfect, and Future
            Perfect are formed with
            <em>haber</em>, a very important auxiliary verb that can be tricky
            for English speakers.
          </div>
        </li>
        <li class="list-group-item list-group-item-action flex-column">
          <h4 class=" w-100 justify-content-between">
            The Preterite vs. Present Perfect:</h4
          >
          <div class="">
            In English, the Present Perfect is used for past actions with
            present relevance. In Spanish,
            <strong>Pretérito Indefinido</strong> (Preterite) is used more for
            actions completed at a specific point in the past, and the
            <strong>Pretérito Perfecto</strong> (Present Perfect) is used more
            for recent actions that affect the present.
          </div>
        </li>
        <li class="list-group-item list-group-item-action flex-column">
          <h4 class=" w-100 justify-content-between"
            >Continuous Tenses:</h4
          >
          <div class="">
            Spanish uses the <em>estar</em> verb with the gerund to express
            continuous actions, just like English uses
            <em>am/is/are + verb-ing</em>.
          </div>
        </li>
        <li class="list-group-item list-group-item-action flex-column">
          <h4 class=" w-100 justify-content-between"
            >Infrequent Use of Future Perfect Continuous:</h4
          >
          <div class="">
            Spanish generally uses the Future Perfect rather than the Future
            Perfect Continuous.
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
  
  <script>
export default {
  data() {
    return {
      tenses: [
        {
          name: "Present Simple",
          english: "I work",
          spanish: "Yo trabajo",
          usage: "Used for actions that are general, habitual, or truths.",
        },
        {
          name: "Present Continuous",
          english: "I am working",
          spanish: "Yo estoy trabajando",
          usage: "Used for actions happening at the moment of speaking.",
        },
        {
          name: "Present Perfect",
          english: "I have worked",
          spanish: "Yo he trabajado",
          usage:
            "Used for actions that happened in the past but are relevant to the present moment.",
        },
        {
          name: "Present Perfect Continuous",
          english: "I have been working",
          spanish: "He estado trabajando",
          usage:
            "Used for actions that began in the past and continue into the present or were recently completed.",
        },
        {
          name: "Past Simple (Preterite)",
          english: "I worked",
          spanish: "Yo trabajé",
          usage: "Used for actions completed at a specific time in the past.",
        },
        {
          name: "Past Continuous",
          english: "I was working",
          spanish: "Yo estaba trabajando",
          usage:
            "Used for actions that were happening at a specific time in the past.",
        },
        {
          name: "Past Perfect",
          english: "I had worked",
          spanish: "Yo había trabajado",
          usage:
            "Used for actions that were completed before another action in the past.",
        },
        {
          name: "Past Perfect Continuous",
          english: "I had been working",
          spanish: "Había estado trabajando",
          usage:
            "Used for actions that were ongoing in the past up until a certain point.",
        },
        {
          name: "Future Simple",
          english: "I will work",
          spanish: "Yo trabajaré",
          usage: "Used for actions that will happen in the future.",
        },
        {
          name: "Future Continuous",
          english: "I will be working",
          spanish: "Yo estaré trabajando",
          usage:
            "Used for actions that will be happening at a specific time in the future.",
        },
        {
          name: "Future Perfect",
          english: "I will have worked",
          spanish: "Yo habré trabajado",
          usage:
            "Used for actions that will be completed before a specific point in the future.",
        },
        {
          name: "Future Perfect Continuous",
          english: "I will have been working",
          spanish: "Habré estado trabajando",
          usage:
            "Used for actions that will be ongoing in the future up until a certain point.",
        },
      ],
    };
  },
};
</script>
  
  <style scoped>
.cheatsheet {
  font-family: Arial, sans-serif;
  padding: 20px;
  color: #333;
}

h1 {
  text-align: center;
  font-size: 2rem;
  margin-bottom: 1rem;
}

.tense-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tense-row {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tense-column {
  margin-bottom: 1rem;
}

.tense-column h3 {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.tense-column p {
  margin: 0.25rem 0;
}

.tense-column strong {
  font-weight: bold;
}

.list-group-item {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 1rem;
  background-color: #fff;
}

.list-group-item h4 {
  margin-bottom: 0.5rem;
  text-align: center;
}
</style>
  